import { FC, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { deleteBot } from '@/services/bots';

interface IDeleteBotDialogProps {
  token: string;
  botName: string;
  onDeleted: () => void;
}

/**
 * Permanently delete the selected bot. The name has to be typed to confirm, because the
 * widget stops working everywhere it is embedded the moment this succeeds. A failure is
 * shown in the dialog — the control this replaces closed itself and logged the error to
 * the console, so customers never learned a delete had not happened (mb-server#6280).
 */
const DeleteBotDialog: FC<IDeleteBotDialogProps> = ({ token, botName, onDeleted }) => {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const expected = botName.trim();
  const confirmed = expected.length > 0 && typed.trim() === expected;

  const reset = () => {
    setTyped('');
    setError(null);
  };

  const handleDelete = async () => {
    if (!confirmed || deleting) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteBot(token);
      setOpen(false);
      reset();
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The bot could not be deleted. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
        onClick={() => { reset(); setOpen(true); }}
        disabled={!token}
      >
        <Trash2 className="mr-2 h-4 w-4" />
        Delete bot
      </Button>
      <AlertDialog open={open} onOpenChange={(next) => { if (!deleting) setOpen(next); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{expected || 'this bot'}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the bot with its training files, links and settings. The
              widget stops working on every site where it is embedded, including visitor tracking.
              To only stop it showing, turn on &ldquo;Hide chat widget&rdquo; above instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <label htmlFor="delete-bot-confirm" className="text-sm text-slate-600">
              Type <span className="font-semibold text-slate-900">{expected}</span> to confirm
            </label>
            <Input
              id="delete-bot-confirm"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              disabled={deleting}
            />
            {error && (
              <p role="alert" className="text-sm text-red-600">{error}</p>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={!confirmed || deleting}
            >
              {deleting ? 'Deleting…' : 'Delete bot'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default DeleteBotDialog;
